import { useEffect, useState } from 'react';
import type { TaskRow, StepRow, ListRow } from '../api/types';
import { tasksApi, stepsApi } from '../api/client';
import { toLocalInput, fromLocalInput, importanceLabel } from '../lib/format';
import { formatDate } from '../lib/time';
import { toast } from '../store/toast';
import { haptic } from '../hooks/useHaptics';

interface Props {
  task: TaskRow;
  lists: ListRow[];
  onClose: () => void;
  /** 更新成功回调, 收到服务端返回的最新 row (可选) */
  onUpdated: (updated?: TaskRow) => void;
  onDeleted: () => void;
}

export function TaskDetail({ task, lists, onClose, onUpdated, onDeleted }: Props) {
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [newStep, setNewStep] = useState('');
  const [saving, setSaving] = useState(false);
  const [localTitle, setLocalTitle] = useState(task.title);
  const [localNote, setLocalNote] = useState(task.note ?? '');

  useEffect(() => {
    setLocalTitle(task.title);
    setLocalNote(task.note ?? '');
  }, [task.id, task.title, task.note]);

  useEffect(() => {
    if (!task.id) return;
    stepsApi
      .list(task.id)
      .then(setSteps)
      .catch(() => setSteps([]));
  }, [task.id]);

  // 用 task.version 防止覆盖其他设备的并发更新
  // 乐观更新: 改完字段立刻用本地时间戳同步给父组件, 底部"更新: 刚刚"立即刷新
  // (用户感知延迟 = 0)
  // 服务端响应回来再用真 row 覆盖 (version 修正)
  const update = async (patch: Record<string, unknown>) => {
    setSaving(true);
    // 乐观: 立刻用本地时间戳更新本地 task
    const optimistic = {
      ...task,
      ...patch,
      updated_at: Date.now(),
      // 假设服务端会 +1 version
      version: task.version + 1,
    };
    onUpdated(optimistic);
    try {
      const updated = await tasksApi.update(task.id, { ...patch, version: task.version });
      // 服务端真值覆盖
      onUpdated(updated);
    } catch (err) {
      alert(`更新失败: ${(err as Error).message}`);
      // 失败时 refresh 拉真值
      onUpdated({ ...task }); // 触发 useEffect 重拉
    } finally {
      setSaving(false);
    }
  };

  const debouncedSaveTitle = () => {
    if (localTitle.trim() && localTitle !== task.title) {
      update({ title: localTitle.trim() });
    }
  };

  const debouncedSaveNote = () => {
    if (localNote !== (task.note ?? '')) {
      update({ note: localNote || null });
    }
  };

  const toggleImportance = () => {
    update({ importance: (task.importance + 1) % 3 });
  };

  const toggleStarred = () => {
    haptic('selection');
    update({ starred: task.starred ? 0 : 1 });
  };
  const toggleMyDay = async () => {
    const willAdd = !task.my_day;
    try {
      const updated = willAdd
        ? await tasksApi.addToMyDay(task.id)
        : await tasksApi.removeFromMyDay(task.id);
      onUpdated(updated);
      if (willAdd) {
        toast({
          kind: 'success',
          text: '已加入"我的一天"',
          actionLabel: '去看看',
          to: '/',
        });
      } else {
        toast({ kind: 'info', text: '已从"我的一天"移除' });
      }
    } catch (err) {
      toast({ kind: 'error', text: `操作失败: ${(err as Error).message}` });
    }
  };
  const toggleCompleted = () => {
    haptic(task.completed ? 'light' : 'success');
    update({ completed: task.completed ? 0 : 1 });
  };

  const toggleArchive = async () => {
    const wasArchived = !!task.archived_at;
    try {
      const updated = wasArchived
        ? await tasksApi.unarchive(task.id, task.version)
        : await tasksApi.archive(task.id, task.version);
      onUpdated(updated);
      toast({
        kind: 'info',
        text: wasArchived ? '已取消归档' : '已归档(在侧边栏"已归档"中查看)',
      });
    } catch (err) {
      toast({ kind: 'error', text: `操作失败: ${(err as Error).message}` });
    }
  };

  const changeList = (listId: string) => update({ list_id: listId });

  const addStep = async () => {
    if (!newStep.trim()) return;
    try {
      const row = await stepsApi.create(task.id, { title: newStep.trim() });
      setSteps((s) => [...s, row]);
      setNewStep('');
    } catch (err) {
      alert(`添加子任务失败: ${(err as Error).message}`);
    }
  };

  const toggleStep = async (step: StepRow) => {
    const updated = await stepsApi.update(step.id, { completed: step.completed ? 0 : 1 });
    setSteps((s) => s.map((x) => (x.id === step.id ? updated : x)));
  };

  const deleteStep = async (step: StepRow) => {
    await stepsApi.remove(step.id);
    setSteps((s) => s.filter((x) => x.id !== step.id));
  };

  const deleteTask = async () => {
    haptic('warning');
    if (!confirm(`删除任务 "${task.title}"?`)) return;
    try {
      await tasksApi.remove(task.id);
      haptic('success');
      onDeleted();
    } catch (err) {
      haptic('error');
      alert(`删除失败: ${(err as Error).message}`);
    }
  };

  const imp = importanceLabel(task.importance);

  return (
    <div className="fixed inset-0 z-50 flex md:static md:inset-auto md:z-auto">
      {/* 点击空白处关闭: 半透明背景, 仅 mobile/全屏时显示 */}
      <div
        onClick={() => {
          haptic('light');
          onClose();
        }}
        className="absolute inset-0 bg-black/30 md:hidden"
        aria-label="关闭详情"
      />
      <aside className="relative flex h-full w-full shrink-0 flex-col border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 safe-top safe-bottom md:w-[480px] lg:w-[520px] xl:w-[560px]">
      {/* 顶部 */}
      <div className="header-immersive sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 px-4 py-2 dark:border-gray-700 safe-top">
        <span className="text-xs text-gray-400">
          {saving ? '保存中...' : '已保存'}
        </span>
        <button
          onClick={() => {
            haptic('light');
            onClose();
          }}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* 标题 */}
        <input
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          onBlur={debouncedSaveTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          className="w-full bg-transparent text-lg font-semibold outline-none placeholder:text-gray-300"
          placeholder="任务名"
        />

        {/* 状态栏 */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <button
            onClick={toggleCompleted}
            className={`flex items-center gap-1 rounded px-2 py-1 text-xs ${
              task.completed
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40'
                : 'border border-gray-300 text-gray-600 dark:border-gray-600'
            }`}
          >
            {task.completed ? '✓ 已完成' : '○ 标记完成'}
          </button>
          <button
            onClick={toggleStarred}
            className={`rounded px-2 py-1 text-xs ${
              task.starred ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {task.starred ? '★ 重要' : '☆ 标记重要'}
          </button>
          <button
            onClick={toggleImportance}
            className={`rounded px-2 py-1 text-xs ${imp.color} hover:bg-gray-100 dark:hover:bg-gray-700`}
            title="点击循环切换重要性"
          >
            {imp.label}优先级
          </button>
          <button
            onClick={toggleMyDay}
            className={`rounded px-2 py-1 text-xs ${
              task.my_day ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {task.my_day ? '☀️ 我的一天' : '加入我的一天'}
          </button>
          <button
            onClick={toggleArchive}
            className={`rounded px-2 py-1 text-xs ${
              task.archived_at
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30'
                : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {task.archived_at ? '🗄 取消归档' : '🗄 归档'}
          </button>
        </div>

        {/* 列表归属 */}
        <div>
          <label className="text-xs uppercase text-gray-400">列表</label>
          <select
            value={task.list_id}
            onChange={(e) => changeList(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700"
          >
            {lists.map((l) => (
              <option key={l.id} value={l.id}>
                {l.icon ?? '📁'} {l.name}
              </option>
            ))}
          </select>
        </div>

        {/* 截止 + 提醒 */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase text-gray-400">截止</label>
            <input
              type="datetime-local"
              value={toLocalInput(task.due_date)}
              onChange={(e) => update({ due_date: fromLocalInput(e.target.value) })}
              className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700"
            />
            {task.due_date && (
              <button
                onClick={() => update({ due_date: null })}
                className="mt-1 text-xs text-gray-400 hover:text-red-500"
              >
                清除
              </button>
            )}
          </div>
          <div>
            <label className="text-xs uppercase text-gray-400">提醒</label>
            <input
              type="datetime-local"
              value={toLocalInput(task.reminder_at)}
              onChange={(e) => update({ reminder_at: fromLocalInput(e.target.value) })}
              className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700"
            />
            {task.reminder_at && (
              <button
                onClick={() => update({ reminder_at: null })}
                className="mt-1 text-xs text-gray-400 hover:text-red-500"
              >
                清除
              </button>
            )}
          </div>
        </div>

        {/* 备注 */}
        <div>
          <label className="text-xs uppercase text-gray-400">备注</label>
          <textarea
            value={localNote}
            onChange={(e) => setLocalNote(e.target.value)}
            onBlur={debouncedSaveNote}
            placeholder="添加备注"
            rows={4}
            className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700"
          />
        </div>

        {/* 子任务 */}
        <div>
          <label className="text-xs uppercase text-gray-400">
            子任务 ({steps.filter((s) => s.completed).length}/{steps.length})
          </label>
          <div className="mt-1 space-y-1">
            {steps.map((s) => (
              <div key={s.id} className="group flex items-center gap-2">
                <button
                  onClick={() => toggleStep(s)}
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 ${
                    s.completed
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {!!s.completed && (
                    <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
                <span
                  className={`flex-1 text-sm ${s.completed ? 'line-through text-gray-400' : ''}`}
                >
                  {s.title}
                </span>
                <button
                  onClick={() => deleteStep(s)}
                  className="hidden text-xs text-gray-400 hover:text-red-500 group-hover:block"
                >
                  ✕
                </button>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-1">
              <span className="text-gray-400">+</span>
              <input
                value={newStep}
                onChange={(e) => setNewStep(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addStep();
                }}
                placeholder="添加子任务"
                className="flex-1 rounded border-0 bg-transparent text-sm outline-none placeholder:text-gray-300"
              />
            </div>
          </div>
        </div>

        {/* 元信息 */}
        <div className="space-y-1 text-xs text-gray-400">
          <div>创建: {formatDate(task.created_at)}</div>
          <div>更新: {formatDate(task.updated_at)}</div>
        </div>

        {/* 删除按钮 */}
        <button
          onClick={deleteTask}
          className="w-full rounded border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
        >
          删除任务
        </button>
      </div>
      </aside>
    </div>
  );
}